import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api } from "@/App";
import { toast } from "sonner";
import { DollarSign, History, Pencil, Save, X } from "lucide-react";

const fmt = (v) => new Intl.NumberFormat("es-CL", { style: "currency", currency: "CLP", maximumFractionDigits: 0 }).format(v || 0);
const parse = (s) => parseInt(String(s).replace(/[^0-9]/g, "")) || 0;

const MONTOS = [
  { key: "monto_principal", label: "Monto Principal", desc: "Documento fundante", color: "from-blue-500 to-blue-600" },
  { key: "costo_inversion", label: "Costo Inversión", desc: "Daño emergente", color: "from-violet-500 to-violet-600" },
  { key: "lucro_cesante", label: "Lucro Cesante", desc: "Lo perdido", color: "from-orange-500 to-orange-600" },
  { key: "dano_moral", label: "Daño Moral", desc: "Indemnización", color: "from-rose-500 to-rose-600" },
  { key: "reajuste_ipc", label: "Reajuste IPC", desc: "Actualización precios", color: "from-cyan-500 to-cyan-600" },
  { key: "costas_procesales", label: "Costas Procesales", desc: "Gastos proceso", color: "from-slate-500 to-slate-600" },
];

export default function ValorizacionPanel({ casoId, valorizacion, onUpdate }) {
  const [editing, setEditing] = useState(false);
  const [showLog, setShowLog] = useState(false);
  const [logs, setLogs] = useState([]);
  const [editValues, setEditValues] = useState({});
  const [motivo, setMotivo] = useState("");
  const [saving, setSaving] = useState(false);

  const val = valorizacion || {};
  const total = MONTOS.reduce((s, m) => s + (val[m.key] || 0), 0);

  const startEdit = () => { setEditValues({ ...val }); setMotivo(""); setEditing(true); };

  const handleSave = async () => {
    if (!motivo.trim()) { toast.error("Ingresa un motivo para el cambio"); return; }
    setSaving(true);
    try {
      await api.put(`/casos/${casoId}/valorizacion`, { valorizacion: editValues, motivo: motivo.trim() });
      toast.success("Valorización actualizada");
      setEditing(false);
      onUpdate();
    } catch { toast.error("Error actualizando"); }
    finally { setSaving(false); }
  };

  const loadLogs = async () => {
    try {
      const res = await api.get(`/casos/${casoId}/valorizacion-log`);
      setLogs(res.data);
      setShowLog(true);
    } catch { toast.error("Error cargando historial"); }
  };

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString("es-CL", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "";

  return (
    <>
      <Card className="border-0 shadow-sm" data-testid="valorizacion-panel">
        <CardHeader className="pb-2 px-5 pt-5">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center">
                <DollarSign className="h-4 w-4 text-white" />
              </div>
              Valorización
            </CardTitle>
            <div className="flex gap-1">
              <Button variant="ghost" size="sm" onClick={loadLogs} className="h-7 px-2 text-xs text-slate-400 hover:text-slate-600" data-testid="show-log-btn">
                <History className="h-3.5 w-3.5 mr-1" /> Log
              </Button>
              {!editing && (
                <Button variant="ghost" size="sm" onClick={startEdit} className="h-7 px-2 text-xs text-slate-400 hover:text-teal-600" data-testid="edit-valorizacion-btn">
                  <Pencil className="h-3.5 w-3.5 mr-1" /> Editar
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="px-5 pb-5">
          {editing ? (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                {MONTOS.map(m => (
                  <div key={m.key}>
                    <Label className="text-[11px] font-medium text-slate-500">{m.label}</Label>
                    <div className="relative">
                      <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-300 text-xs">$</span>
                      <Input
                        className="pl-6 h-8 text-sm"
                        value={editValues[m.key] === 0 ? "" : (editValues[m.key] || "").toLocaleString("es-CL")}
                        onChange={(e) => setEditValues({ ...editValues, [m.key]: parse(e.target.value) })}
                        data-testid={`edit-${m.key}`}
                      />
                    </div>
                  </div>
                ))}
              </div>
              <div>
                <Label className="text-[11px] font-medium text-slate-500">Motivo del cambio *</Label>
                <Textarea value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder="¿Por qué se modifican los montos?" rows={2} className="text-sm" data-testid="motivo-cambio" />
              </div>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" size="sm" onClick={() => setEditing(false)} className="h-7"><X className="h-3 w-3 mr-1" /> Cancelar</Button>
                <Button size="sm" onClick={handleSave} disabled={saving} className="h-7 bg-teal-600 hover:bg-teal-700 text-white" data-testid="save-valorizacion-btn">
                  <Save className="h-3 w-3 mr-1" /> {saving ? "..." : "Guardar"}
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                {MONTOS.map(m => (
                  <div key={m.key} className="flex items-center gap-2 p-2 rounded-lg bg-slate-50/80" data-testid={`val-${m.key}`}>
                    <div className={`w-1 h-8 rounded-full bg-gradient-to-b ${m.color}`} />
                    <div className="min-w-0 flex-1">
                      <p className="text-[10px] text-slate-400 truncate">{m.label}</p>
                      <p className="text-sm font-semibold text-slate-700">{fmt(val[m.key])}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex items-center justify-between pt-2 border-t border-dashed border-slate-200">
                <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">Total</span>
                <span className="text-xl font-bold text-teal-700" data-testid="total-valorizacion-display">{fmt(total)}</span>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={showLog} onOpenChange={setShowLog}>
        <DialogContent className="max-w-lg max-h-[70vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-base">Historial de Cambios</DialogTitle>
            <DialogDescription className="text-xs">Registro de modificaciones a la valorización</DialogDescription>
          </DialogHeader>
          <div className="space-y-2" data-testid="valorizacion-log-list">
            {logs.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-6">Sin cambios registrados</p>
            ) : logs.map(log => (
              <div key={log.id} className="border rounded-lg p-3 text-xs">
                <div className="flex justify-between mb-1.5">
                  <span className="font-medium text-slate-600">{log.usuario_email}</span>
                  <span className="text-slate-400">{fmtDate(log.fecha)}</span>
                </div>
                <p className="text-slate-500 italic mb-2">"{log.motivo}"</p>
                <div className="flex flex-wrap gap-1">
                  {MONTOS.map(m => {
                    const prev = (log.valor_anterior || {})[m.key] || 0;
                    const curr = (log.valor_nuevo || {})[m.key] || 0;
                    return prev !== curr ? (
                      <span key={m.key} className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-amber-50 rounded text-[10px]">
                        <span className="font-medium">{m.label}:</span>
                        <span className="text-red-500 line-through">{fmt(prev)}</span>
                        <span className="text-emerald-600">{fmt(curr)}</span>
                      </span>
                    ) : null;
                  })}
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
